import { Fragment, type ReactNode, useMemo, useState } from 'react';
import {
  type ApprovedCaseSummary,
  AssemblyDnd,
  CaseDropTarget,
  Dialog,
  DragHandle,
  ExcludeSubsectionConfirm,
  GeneralInstructions,
  type IncludeState,
  InlineNodeCreator,
  type InlineNodeCreatorVariant,
  NovelBadge,
  SortableList,
  SortableNode,
  StructureStateChip,
  SubsectionRow,
  TestCaseTable,
  UATPage,
  type UATSectionData,
  IncludeExcludeControl,
} from '@suvoda-uat/ui-suvoda';
import { ApiError, type TestCase } from '../lib/api-client.js';
import {
  type CompletionRollup,
  type SortOrder,
  casesBySubsectionId,
  deriveAssemblyView,
  markedProgress,
  approvedCasesIn,
  qualifyingExactMatchCases,
  scopeAdvisoryChip,
  sectionCompletion,
  statsLine,
  structureChip,
  subsectionCompletion,
  subsectionPresentation,
  summariseSelection,
  testCaseDisplayName,
  toTestCaseRow,
} from '../lib/assembly.js';
import { canAuthor } from '../lib/auth.js';
import {
  useClearInclusion,
  useCreateSection,
  useCreateSubsection,
  useCreateTestCase,
  useExcludeSubsectionCascade,
  useExcludeTestCase,
  useIncludeTestCase,
  useMarkComplete,
  useMoveSection,
  useMoveSubsection,
  useMoveTestCase,
  useSetTestCaseRoles,
  useTestScript,
  useIncludeSection,
  useExcludeSection,
  useClearSectionInclusion,
  useIncludeSubsection,
  useExcludeSubsection,
  useClearSubsectionInclusion,
  useUpdateSubsectionInstructions,
} from '../lib/queries.js';

export type AssemblyReviewViewProps = {
  trialId: string;
  /** Section ids that should start expanded, used by route/stateful stories. */
  initialOpenSectionIds?: readonly string[];
  /** Subsection ids that should start expanded, used by route/stateful stories. */
  initialOpenSubsectionIds?: readonly string[];
  /** Test-ID filter to seed on mount (F-024 restore). '' / unset = no filter (FR-012). */
  initialQuery?: string;
  /**
   * Controlled Test-ID/name filter. When provided, the view is a CONTROLLED
   * filter driven by the parent (so the navbar search can drive it) — the
   * internal state is bypassed and `onQueryChange` is the single source of
   * truth. Absent ⇒ the view owns its own filter, seeded from `initialQuery`.
   */
  query?: string;
  /** Hand a selected Test Case id back to the route, which navigates to its detail. */
  onSelectTestCase?: (testCaseId: string) => void;
  /** Fires with the open-Section id set when a section is toggled (F-024 capture, FR-001). */
  onOpenSectionIdsChange?: (ids: readonly string[]) => void;
  /** Fires with the open-Subsection id set when a subsection is toggled (F-024 capture, FR-002). */
  onOpenSubsectionIdsChange?: (ids: readonly string[]) => void;
  /** Fires with the Test-ID filter on every change (F-024 capture, FR-012). */
  onQueryChange?: (query: string) => void;
};

// The read-only Test Case list these helpers iterate. Accepted read-only so they
// compose with BOTH the raw cached tree and the F-023 display-annotated tree:
// `deriveAssemblyView` now returns `readonly` sections whose cases carry a
// displayTestId, and a `readonly DisplayTestCase[]` is assignable here.
type SubsectionCases = { readonly testCases: readonly TestCase[] };

/**
 * F-017 (US2): a node's title-suffix slot carries BOTH the "N" novel badge
 * (origin === 'novel') and the honest structure-state chip when the node is in
 * a non-default state (excluded / empty / needs-authoring). Returns undefined
 * when neither applies — the common case — so the row geometry is untouched.
 */
const titleSuffixFor = (
  isNovel: boolean,
  note: ReturnType<typeof structureChip>,
  // F-151 (US2): an optional quiet "advisory" chip alongside the structure
  // chip — a below-gate TRS verdict the reviewer should see but that did NOT
  // auto-exclude (FR-020).
  advisoryNote: ReturnType<typeof scopeAdvisoryChip> = null,
): ReactNode | undefined => {
  if (!isNovel && note === null && advisoryNote === null) return undefined;
  return (
    <span className="inline-flex items-center gap-1.5">
      {isNovel ? <NovelBadge /> : null}
      {note ? <StructureStateChip note={note} /> : null}
      {advisoryNote ? <StructureStateChip note={advisoryNote} /> : null}
    </span>
  );
};

// F-010 (FR-019): completability is gated on CONTENT, not roles — the
// role-required-to-complete gate is gone. A section shows the Completed column
// when any case has content (the server's only remaining markComplete gate).
const sectionHasCompletableCases = (section: {
  readonly subsections: readonly SubsectionCases[];
}): boolean =>
  section.subsections.some((sub) =>
    sub.testCases.some((testCase) => testCase.content.rows.length > 0),
  );

const createTestCaseIdempotencyKey = (): string => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `create-test-case-${Date.now()}`;
};

const nodeIdempotencyKey = (): string => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `create-node-${Date.now()}`;
};

// Stable per-gap keys so a create's pending/error state scopes to exactly the
// InlineNodeCreator that fired it (`TOP` = insert before the first sibling).
const sectionGapKey = (afterSectionId: string | undefined): string =>
  `sec:${afterSectionId ?? 'TOP'}`;
const subsectionGapKey = (sectionId: string, afterSubsectionId: string | undefined): string =>
  `sub:${sectionId}:${afterSubsectionId ?? 'TOP'}`;

const mapCreateError = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 409) return 'The list changed while you were editing — please try again.';
    if (error.status === 422) return 'That name is not valid — please try another.';
    if (error.status === 403) return 'You do not have permission to add here.';
  }
  return 'Could not add — please try again.';
};

const mapMoveError = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 409) {
      return 'The script changed while you were dragging — it has been refreshed, please retry.';
    }
    if (error.status === 422) return 'That move is not allowed — the list has been restored.';
    if (error.status === 403) return 'You do not have permission to reorder.';
  }
  return 'Could not move — please try again.';
};

// F-029 (R4/FR-003): a per-case action that fails rolls back in the hook; the
// view surfaces a non-blocking, status-mapped notice (never echoing case
// content, §IX). 409 means a concurrent change (the hook also refetches once to
// reconcile, R5); 422 a rejected change; 403 a permission gap.
const mapCaseActionError = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 409) {
      return 'This case changed while you were editing — it has been refreshed, please retry.';
    }
    if (error.status === 422) return 'That change is not allowed — it has been reverted.';
    if (error.status === 403) return 'You do not have permission to make that change.';
  }
  return 'Could not save — please try again.';
};

/**
 * Assembly review view wired to live F-002 data (US1/US2). Reads the Trial's
 * ListTestScript and renders the `ui-suvoda` UATPage: a header summarising the
 * engine's recommendations, then collapsible Sections → Subsections → the
 * engine-scored Test Case rows. Per-case and subsection triage map to the US2
 * mutation hooks (each carrying the Test Case `version` as If-Match); a row
 * click hands the Test Case id to the route for navigation. F-011 adds novel
 * Section/Subsection insertion bars (Editor+ only; the server fail-closes too)
 * and an "N" badge on reviewer-added nodes. Thin glue — the route supplies
 * `trialId` + navigation.
 */
export function AssemblyReviewView({
  trialId,
  initialOpenSectionIds = [],
  initialOpenSubsectionIds = [],
  initialQuery = '',
  query: controlledQuery,
  onSelectTestCase,
  onOpenSectionIdsChange,
  onOpenSubsectionIdsChange,
  onQueryChange,
}: AssemblyReviewViewProps) {
  const script = useTestScript(trialId);
  const include = useIncludeTestCase(trialId);
  const exclude = useExcludeTestCase(trialId);
  const clearInclusion = useClearInclusion(trialId);
  const markComplete = useMarkComplete(trialId);
  const setRoles = useSetTestCaseRoles(trialId);
  const createTestCase = useCreateTestCase(trialId);
  const createSection = useCreateSection(trialId);
  const createSubsection = useCreateSubsection(trialId);
  const moveSection = useMoveSection(trialId);
  const moveSubsection = useMoveSubsection(trialId);
  const moveTestCase = useMoveTestCase(trialId);
  // F-145 (US1/US4): the optimistic all-or-nothing subsection-exclude cascade
  // (distinct from the instructional-node `useExcludeSubsection` decision below).
  const excludeSubsectionCascade = useExcludeSubsectionCascade(trialId);
  const [openSubs, setOpenSubs] = useState<Set<string>>(() => new Set(initialOpenSubsectionIds));
  const [activeCreator, setActiveCreator] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  // F-029 (FR-003): the per-case-action error surface (mirror of moveError).
  const [caseActionError, setCaseActionError] = useState<string | null>(null);
  // F-145 (US2): the pending warn-and-confirm exclude. When set, the house Dialog
  // names the approved cases that excluding `cases` would discard; the cascade
  // fires only on confirm (FR-005/006). `null` = no confirmation in flight.
  const [pendingExclude, setPendingExclude] = useState<{
    readonly subsectionTitle: string;
    readonly cases: readonly TestCase[];
    readonly approvedCases: readonly ApprovedCaseSummary[];
  } | null>(null);
  const onCaseActionError = (error: unknown) => setCaseActionError(mapCaseActionError(error));
  // F-024: seed the Test-ID filter from the restored place (FR-012). When a
  // controlled `query` is supplied (the navbar search drives it) the internal
  // state is bypassed.
  const [internalQuery, setInternalQuery] = useState(initialQuery);
  const query = controlledQuery ?? internalQuery;
  // F-022 Assembly View Lens: per-visit, never-persisted component state — it
  // survives in-page refetches (React Query re-renders without resetting it) and
  // resets on unmount (§XI exemption: component-local UI state, R1/R11).
  const [hideCompleted, setHideCompleted] = useState(false);
  // Sort cycles manual → testIdAsc → testIdDesc → manual; 'manual' is the
  // F-015 persisted Script Order, which the lens NEVER writes (read-only).
  const [sortOrder, setSortOrder] = useState<SortOrder>('manual');
  const cycleSort = () =>
    setSortOrder((current) =>
      current === 'manual' ? 'testIdAsc' : current === 'testIdAsc' ? 'testIdDesc' : 'manual',
    );

  // As-you-type Test-ID filter (the dashboard-search pattern): only matching
  // Test Cases survive, subsections/sections with no survivors drop out, and
  // the remaining tree is forced open. The user's toggle state is untouched
  // underneath, so clearing the query restores it.
  const needle = query.trim().toLowerCase();
  const filtering = needle !== '';

  // Editor+ may author novel nodes (mirrors FinaliseControl's Approver gate);
  // viewers see badges but no insertion affordances. F-022: position-based
  // structural edits gate on ANY altered view — search, Hide, or a non-manual
  // Sort — because gaps/positions in a filtered or re-sorted tree are not real
  // insertion/drag targets. Identity-addressed actions (Add Test Case, per-row
  // triage/complete/roles) are never lens-gated (FR-008/FR-009).
  const canEdit = canAuthor();
  const viewAltered = filtering || hideCompleted || sortOrder !== 'manual';
  const showCreators = canEdit && !viewAltered;
  // Drag-and-drop reorder (feature 015): editor-gated like the creators, and off
  // whenever the view is altered — positions in a transformed tree are not real.
  const canDrag = canEdit && !viewAltered;
  const moveBusy = moveSection.isPending || moveSubsection.isPending || moveTestCase.isPending;
  // F-013 (FR-010): Instructional-Section Decision mutations (If-Match = the
  // aggregate version; a decision can regress a finalised trial).
  const includeSectionDecision = useIncludeSection(trialId);
  const excludeSectionDecision = useExcludeSection(trialId);
  const clearSectionDecision = useClearSectionInclusion(trialId);
  const includeSubsectionDecision = useIncludeSubsection(trialId);
  const excludeSubsectionDecision = useExcludeSubsection(trialId);
  const clearSubsectionDecision = useClearSubsectionInclusion(trialId);
  // F-113 (US3): edit a subsection's General Instructions (If-Match = the aggregate
  // version, like the node decisions; lifecycle-neutral — never regresses).
  const updateInstructions = useUpdateSubsectionInstructions(trialId);

  const data = script.data;
  // id → Test Case, for the current `version` on a triage action (rebuilt when a
  // mutation invalidates + refetches the script).
  const byId = useMemo(() => {
    const map = new Map<string, TestCase>();
    for (const section of data?.sections ?? []) {
      for (const sub of section.subsections) {
        for (const testCase of sub.testCases) map.set(testCase.id, testCase);
      }
    }
    return map;
  }, [data]);

  // Feature 025: the Completion Rollup for each Section and Subsection, derived
  // from the RAW `data.sections` — NEVER the 022-lens-filtered `visibleSections`
  // below (hide/search remove rows ⇒ a wrong denominator, FR-007/SC-004) — and
  // keyed by node id so the lens-filtered render can look each up. Mirrors how
  // `markedProgress(data)` already runs on the raw payload.
  const { rollupBySectionId, rollupBySubsectionId } = useMemo(() => {
    const bySection = new Map<string, CompletionRollup>();
    const bySubsection = new Map<string, CompletionRollup>();
    for (const section of data?.sections ?? []) {
      bySection.set(section.id, sectionCompletion(section));
      for (const sub of section.subsections) {
        bySubsection.set(sub.id, subsectionCompletion(sub));
      }
    }
    return { rollupBySectionId: bySection, rollupBySubsectionId: bySubsection };
  }, [data]);

  // Canonical cases per subsection, built from the UNFILTERED tree (FR-013,
  // feature 026): subsection-level actions cascade over these, never the
  // lens-filtered `visibleSections` rows, so a reverse/flip reaches every case
  // even when a Hide/Sort/search lens hides some.
  const casesBySub = useMemo(
    () => (data ? casesBySubsectionId(data) : new Map<string, readonly TestCase[]>()),
    [data],
  );

  // The Assembly View Lens (F-022): search ∘ hide ∘ grouping-drop ∘ sort, as one
  // pure derivation. Hide/Sort land their state in US1/US2; today the lens
  // carries only the search needle, so this is behaviour-preserving.
  const { visibleSections, emptyReason } = useMemo(
    () =>
      deriveAssemblyView(data?.sections ?? [], {
        needle,
        hideCompleted,
        sortOrder,
      }),
    [data, needle, hideCompleted, sortOrder],
  );

  // F-114 (#114, US3): the per-trial resolved value map (canonicalId → value),
  // supplied to every GI card so its placeholders render real values where the
  // system resolved one and display names elsewhere (FR-018). Only RESOLVED
  // entries (a `value` present) enter the map; the rest fall back to display name.
  const placeholderValues = useMemo(
    () =>
      new Map<string, string>(
        (data?.placeholderResolution?.values ?? []).flatMap((resolved) =>
          resolved.value === undefined ? [] : [[resolved.canonicalId, resolved.value] as const],
        ),
      ),
    [data?.placeholderResolution],
  );

  if (!data) {
    return <div className="mx-6 my-4 text-md text-text-secondary">Loading the test script…</div>;
  }

  const toggleSub = (id: string, next: boolean) => {
    const out = new Set(openSubs);
    if (next) out.add(id);
    else out.delete(id);
    setOpenSubs(out);
    // Write-through the user's real open-Subsection set (event handler, not an
    // effect — never fires on mount, never emits the filter-forced view). F-024.
    onOpenSubsectionIdsChange?.([...out]);
  };

  // Wrap the search setter so the route can capture the filter (FR-012). When
  // uncontrolled, the internal state drives the live filtered view; when
  // controlled, the parent owns it. `onQueryChange` always fires (capture /
  // controlled source of truth).
  const handleQueryChange = (value: string) => {
    if (controlledQuery === undefined) setInternalQuery(value);
    onQueryChange?.(value);
  };

  const triage = (testCaseId: string, next: IncludeState) => {
    const testCase = byId.get(testCaseId);
    if (!testCase || next === 'none') return;
    (next === 'included' ? include : exclude).mutate(
      { testCaseId, version: testCase.version },
      { onError: onCaseActionError },
    );
  };

  const complete = (testCaseId: string) => {
    const testCase = byId.get(testCaseId);
    // F-038 US1 (FR-001): never request completion for an undecided case. The
    // row affordance is already disabled for undecided cases (TestCaseTable), but
    // guard the handler too so no path optimistically completes without a decision.
    if (!testCase || testCase.inclusionDecision.state === 'undecided') return;
    markComplete.mutate({ testCaseId, version: testCase.version }, { onError: onCaseActionError });
  };

  // F-010 (T037): full-list role replace from the list entry point. Carries the
  // row's current `version` as If-Match, like every other triage write.
  const setRolesFor = (testCaseId: string, next: string[]) => {
    const testCase = byId.get(testCaseId);
    if (testCase)
      setRoles.mutate(
        { testCaseId, roles: next, version: testCase.version },
        { onError: onCaseActionError },
      );
  };

  // Deselect: clear a row's Inclusion Decision back to undecided (the AI
  // recommendation outline remains). Single-item If-Match write.
  const clearInclude = (testCaseId: string) => {
    const testCase = byId.get(testCaseId);
    if (testCase)
      clearInclusion.mutate(
        { testCaseId, version: testCase.version },
        { onError: onCaseActionError },
      );
  };

  // F-145 (US1, FR-001/003): the ONLY subsection-level bulk action is exclude.
  // It cascades the existing per-case exclude over the subsection's CANONICAL
  // children (FR-013 — the unfiltered tree, never the lens-filtered visible rows)
  // optimistically and all-or-nothing (the hook compensates + reconciles on any
  // failure, US4).
  const runExcludeCascade = (cases: readonly TestCase[]) => {
    excludeSubsectionCascade.mutate({ cases }, { onError: onCaseActionError });
  };

  // F-145 (US2, FR-005/007): excluding a subsection that holds approved (included)
  // cases first opens a warn-and-confirm dialog naming them (the cascade fires
  // only on confirm); with no approved cases it excludes directly. Always reads
  // the CANONICAL children, never `sub.testCases` (which is the lens-filtered row
  // set) — so the guard + cascade reach every case regardless of any active view.
  const requestExcludeSubsection = (subsectionId: string, subsectionTitle: string) => {
    const cases = casesBySub.get(subsectionId) ?? [];
    const approved = approvedCasesIn(cases);
    if (approved.length === 0) {
      runExcludeCascade(cases);
      return;
    }
    setPendingExclude({
      subsectionTitle,
      cases,
      approvedCases: approved.map((testCase) => ({
        id: testCase.id,
        testId: testCase.testId,
        name: testCaseDisplayName(testCase),
      })),
    });
  };

  const confirmExcludeSubsection = () => {
    if (pendingExclude) runExcludeCascade(pendingExclude.cases);
    setPendingExclude(null);
  };

  // F-146: bulk-approve the subsection's byte-for-byte exact matches — the
  // triageSubsection cascade with a qualifyingExactMatchCases filter in front, so
  // ONLY the undecided byte-for-byte cases are included (SC-001), over the
  // CANONICAL cases (FR-005 — incl. any hidden by the lens/search). Each case rides
  // the EXISTING per-case optimistic include op with its own version as If-Match
  // and an independent onError (FR-009/FR-010): the optimistic flip empties the
  // qualifying set so the control vanishes (clarify), and a single case's failure
  // reverts only that case (in the hook) while the others stand. No completion
  // write, no confirmation step.
  const approveExactMatches = (subsectionId: string) => {
    for (const testCase of qualifyingExactMatchCases(casesBySub.get(subsectionId) ?? [])) {
      include.mutate(
        { testCaseId: testCase.id, version: testCase.version },
        { onError: onCaseActionError },
      );
    }
  };

  // FR-002: reverse a whole-subsection decision back to needs-review. Clears
  // EVERY canonical Test Case to `undecided` via the existing per-case
  // clearInclusion op — it NEVER re-applies a decision (no include/exclude), so
  // an unreviewed case is never stamped "approved & included" (SC-002), and it
  // issues no completion write, so completion marks are untouched (FR-010).
  const reverseSubsection = (subsectionId: string) => {
    for (const testCase of casesBySub.get(subsectionId) ?? []) {
      clearInclusion.mutate(
        { testCaseId: testCase.id, version: testCase.version },
        { onError: onCaseActionError },
      );
    }
  };

  // F-038 US3 (FR-010, I-CG-07): the subsection-level "complete all" cascade is
  // DELETED — completion is earned per case, never swept. The subsection indicator
  // is derived read-only from the F-025 rollup (SubsectionRow), with no setter.

  const addTestCase = (subsectionId: string) => {
    createTestCase.mutate({
      subsectionId,
      idempotencyKey: createTestCaseIdempotencyKey(),
    });
  };

  // A single create runs at a time; pending/error scope to the firing gap key so
  // other open editors never flicker pending or borrow an unrelated error.
  const isCreating = createSection.isPending || createSubsection.isPending;

  const addSection = (afterSectionId: string | undefined, title: string) => {
    setActiveCreator(sectionGapKey(afterSectionId));
    setCreateError(null);
    createSection.mutate(
      {
        title,
        idempotencyKey: nodeIdempotencyKey(),
        ...(afterSectionId ? { afterSectionId } : {}),
      },
      { onError: (error) => setCreateError(mapCreateError(error)) },
    );
  };

  const addSubsection = (
    sectionId: string,
    afterSubsectionId: string | undefined,
    title: string,
  ) => {
    setActiveCreator(subsectionGapKey(sectionId, afterSubsectionId));
    setCreateError(null);
    createSubsection.mutate(
      {
        sectionId,
        title,
        idempotencyKey: nodeIdempotencyKey(),
        ...(afterSubsectionId ? { afterSubsectionId } : {}),
      },
      { onError: (error) => setCreateError(mapCreateError(error)) },
    );
  };

  // Feature 015 drag handlers — optimistic mutations (the hooks apply the move
  // locally before the response); failures roll back and surface inline.
  const onMoveError = (error: unknown) => setMoveError(mapMoveError(error));
  const handleMoveSection = (sectionId: string, afterSectionId: string | null) => {
    setMoveError(null);
    moveSection.mutate(
      { sectionId, afterSectionId, idempotencyKey: nodeIdempotencyKey() },
      { onError: onMoveError },
    );
  };
  const handleMoveSubsection = (subsectionId: string, afterSubsectionId: string | null) => {
    setMoveError(null);
    moveSubsection.mutate(
      { subsectionId, afterSubsectionId, idempotencyKey: nodeIdempotencyKey() },
      { onError: onMoveError },
    );
  };
  const handleMoveTestCase = (
    testCaseId: string,
    toSubsectionId: string,
    afterTestCaseId: string | null,
  ) => {
    setMoveError(null);
    moveTestCase.mutate(
      { testCaseId, toSubsectionId, afterTestCaseId, idempotencyKey: nodeIdempotencyKey() },
      { onError: onMoveError },
    );
  };

  const sectionCreator = (afterSectionId: string | undefined) => {
    const key = sectionGapKey(afterSectionId);
    return (
      // 7px top inset; with the 7px section-list gap below it the New Section
      // line sits centred in a 14px section-to-section margin.
      <div className="pt-[7px]">
        <InlineNodeCreator
          label="New Section"
          inputAriaLabel="New section name"
          pending={activeCreator === key && isCreating}
          error={activeCreator === key ? createError : null}
          onCreate={(title) => addSection(afterSectionId, title)}
        />
      </div>
    );
  };

  const subsectionCreator = (
    sectionId: string,
    afterSubsectionId: string | undefined,
    variant: InlineNodeCreatorVariant = 'bar',
    label = 'New Sub-Section',
  ) => {
    const key = subsectionGapKey(sectionId, afterSubsectionId);
    return (
      <InlineNodeCreator
        variant={variant}
        label={label}
        inputAriaLabel="New sub-section name"
        pending={activeCreator === key && isCreating}
        error={activeCreator === key ? createError : null}
        onCreate={(title) => addSubsection(sectionId, afterSubsectionId, title)}
      />
    );
  };

  const sections: UATSectionData[] = visibleSections.map((section) => {
    const showCompleted = sectionHasCompletableCases(section);
    const testCaseCount = section.subsections.reduce((sum, sub) => sum + sub.testCases.length, 0);
    const isInstructive = testCaseCount === 0;
    const isNovel = section.origin === 'novel';
    const isEmptyNovel = isNovel && section.subsections.length === 0;
    const sectionSuffix = titleSuffixFor(isNovel, structureChip(section.structureState));
    // FR-008/US3: show the rollup only when the Section has available cases;
    // otherwise omit it so an empty/instructional/all-excluded node surfaces its
    // inclusion state, never a misleading "0/0".
    const sectionRollup = rollupBySectionId.get(section.id);
    // A purely instructive section (the "Instructive" badge) carries only General
    // Instructions — so it expands straight to that card (Figma 1556-82617), with
    // no subsection rows and no column headers. Rendered as a clean white card.
    const sectionInstructions = (section as { readonly instructions?: string }).instructions;
    const instructiveContent =
      isInstructive && !isNovel && sectionInstructions ? (
        <div className="bg-surface-card">
          {/* #111 instructive-section card (section-level GI is out of F-113 scope —
              subsection-only v1); the placeholder string rides the body. */}
          <GeneralInstructions instructions={{ body: sectionInstructions }} />
        </div>
      ) : null;

    return {
      id: section.id,
      title: section.title,
      subsectionCount: section.subsections.length,
      testCaseCount,
      ...(sectionRollup && sectionRollup.available > 0 ? { completion: sectionRollup } : {}),
      // Novel sections carry the "N" badge, never the "Instructive" pill.
      instructive: isInstructive && !isNovel,
      ...(isInstructive && { subtitle: null }),
      // F-013 (FR-010, SC-007): instructional TEMPLATE nodes get the decision
      // control in the header slot (never inside the toggle button) and a
      // default-included indication; novel nodes never show it.
      ...(section.instructional === true && !isNovel
        ? {
            subtitle:
              (section.inclusionDecision?.state ?? 'undecided') === 'undecided'
                ? 'Included (default)'
                : section.inclusionDecision?.state === 'excluded'
                  ? 'Excluded from the rendered script'
                  : 'Included',
            ...(canEdit
              ? {
                  headerActions: (
                    <IncludeExcludeControl
                      size="compact"
                      recommendation={null}
                      selection={section.inclusionDecision?.state ?? 'undecided'}
                      ariaSuffix={section.title}
                      onInclude={() =>
                        includeSectionDecision.mutate(
                          { sectionId: section.id, version: data.version },
                          { onError: onCaseActionError },
                        )
                      }
                      onExclude={() =>
                        excludeSectionDecision.mutate(
                          { sectionId: section.id, version: data.version },
                          { onError: onCaseActionError },
                        )
                      }
                      onClear={() =>
                        clearSectionDecision.mutate(
                          { sectionId: section.id, version: data.version },
                          { onError: onCaseActionError },
                        )
                      }
                    />
                  ),
                }
              : {}),
          }
        : {}),
      ...(sectionSuffix ? { titleSuffix: sectionSuffix } : {}),
      // Instructive-card sections suppress the Sub Section/Action column header;
      // others keep the 3 columns.
      columns: instructiveContent ? [] : ['Sub Section', 'Action', 'Mark as Completed'],
      ...(showCreators ? { afterRow: sectionCreator(section.id) } : {}),
      content: instructiveContent ?? (
        <div>
          {showCreators &&
            (isEmptyNovel ? (
              // An empty novel Section gets a clear "+ Add Subsections" prompt
              // (Figma 1061-108006); populated sections use hover insertion bars.
              <div className="px-4 py-3">
                {subsectionCreator(section.id, undefined, 'prompt', 'Add Subsections')}
              </div>
            ) : (
              subsectionCreator(section.id, undefined)
            ))}
          {(() => {
            const subsectionItems = section.subsections.map((sub) => {
              // F-145 (FR-001/002): the bulk control's binary presentation —
              // 'excluded' (every case excluded → "Excluded"/Restore) or
              // 'in-script' (the default → "Exclude subsection"). Only 'excluded'
              // carries the decided `status` fill; "In the script" has no accent.
              const presentation = subsectionPresentation(sub.inclusionRollup);
              const subTestCaseCount = sub.testCases.length;
              const hasTestCases = subTestCaseCount > 0;
              // FR-008/US3: omit the rollup when the Subsection has no available
              // cases (empty/instructional/all-excluded) — it shows its inclusion
              // state instead. Derived from the RAW script (R3), looked up by id.
              const subsectionRollup = rollupBySubsectionId.get(sub.id);
              const canTriage = hasTestCases;
              // F-146: the qualifying set (undecided ∧ byte-for-byte) over the
              // CANONICAL cases — its size gates + names the bulk-approve control.
              // Passed only to an editor (FR-006); the control self-hides at 0.
              const exactMatchCount = qualifyingExactMatchCases(
                casesBySub.get(sub.id) ?? [],
              ).length;
              const subSuffix = titleSuffixFor(
                sub.origin === 'novel',
                structureChip(sub.structureState),
                scopeAdvisoryChip(sub.scopeAdvisory),
              );
              const subsectionRow = (
                dragHandle?: ReactNode,
                dragListeners?: Record<string, unknown>,
              ) => (
                <SubsectionRow
                  title={sub.title}
                  testCaseCount={subTestCaseCount}
                  subtitle={
                    sub.instructional === true && sub.origin !== 'novel'
                      ? (sub.inclusionDecision?.state ?? 'undecided') === 'undecided'
                        ? 'Included (default)'
                        : sub.inclusionDecision?.state === 'excluded'
                          ? 'Excluded from the rendered script'
                          : 'Included'
                      : hasTestCases
                        ? undefined
                        : false
                  }
                  {...(sub.instructional === true && sub.origin !== 'novel' && canEdit
                    ? {
                        instructional: true,
                        decisionState: sub.inclusionDecision?.state ?? 'undecided',
                        onDecideInclude: () =>
                          includeSubsectionDecision.mutate(
                            { subsectionId: sub.id, version: data.version },
                            { onError: onCaseActionError },
                          ),
                        onDecideExclude: () =>
                          excludeSubsectionDecision.mutate(
                            { subsectionId: sub.id, version: data.version },
                            { onError: onCaseActionError },
                          ),
                        onClearDecision: () =>
                          clearSubsectionDecision.mutate(
                            { subsectionId: sub.id, version: data.version },
                            { onError: onCaseActionError },
                          ),
                      }
                    : {})}
                  expanded={filtering || openSubs.has(sub.id)}
                  // The Mark-as-Completed column always renders so the subsection
                  // rows align with the always-three-column section header (Figma
                  // 1061-106749). F-038: the indicator is derived read-only from the
                  // rollup (passed below) — no completion control on the row.
                  showCompleted={showCompleted}
                  canTriage={canTriage}
                  canExpand
                  onToggle={(next) => toggleSub(sub.id, next)}
                  onExclude={() => requestExcludeSubsection(sub.id, sub.title)}
                  onReverse={() => reverseSubsection(sub.id)}
                  // F-146 (FR-006): editor-only — a viewer gets no handler, so the
                  // control never renders. The count self-hides it at 0 (FR-007).
                  {...(canEdit
                    ? { exactMatchCount, onApproveExactMatches: () => approveExactMatches(sub.id) }
                    : {})}
                  {...(dragHandle ? { dragHandle } : {})}
                  {...(dragListeners ? { dragListeners } : {})}
                  {...(subSuffix ? { titleSuffix: subSuffix } : {})}
                  {...(presentation === 'excluded' ? { status: 'excluded' as const } : {})}
                  {...(subsectionRollup && subsectionRollup.available > 0
                    ? { completion: subsectionRollup }
                    : {})}
                >
                  <div className="border-t border-border bg-surface-card">
                    {hasTestCases ? (
                      // Feature 026 (FR-004/005): rows stay INTERACTIVE regardless of
                      // the subsection rollup — never the read-only `status` mode — so a
                      // decided subsection keeps its per-row include/exclude/clear control
                      // and the live role chooser, at parity with an undecided one.
                      <TestCaseTable
                        rows={sub.testCases.map(toTestCaseRow)}
                        showCompleted={showCompleted}
                        // The Test ID is the link into the single test-case view:
                        // hand the case's id back to the route, which navigates to
                        // its detail page. The Test-case caret stays a preview toggle.
                        {...(onSelectTestCase ? { onSelectRow: onSelectTestCase } : {})}
                        onIncludeChange={triage}
                        onClearInclude={clearInclude}
                        onCompletedChange={(id, next) => {
                          if (next) complete(id);
                        }}
                        {...(canDrag ? { sortable: { subsectionId: sub.id } } : {})}
                        // F-010 (T037): the live, Allowed-Role-Set-constrained chooser.
                        allowedRoles={data.allowedRoles ?? []}
                        onRolesChange={setRolesFor}
                        // Hover-revealed "+ New Test Case" insertion line between
                        // rows (Figma 1504-74035) replaces the old Add button.
                        onInsertCase={() => addTestCase(sub.id)}
                        inserting={createTestCase.isPending}
                        // F-113 (#41/UAT-13): the General Instructions block above the
                        // table (Figma 1061-101043), rendered from the now-typed optional
                        // subsection value; absent ⇒ no block (FR-003). onSave (US3) PATCHes
                        // the edited body with the aggregate version as If-Match (canEdit only).
                        {...(sub.instructions
                          ? {
                              instructions: (
                                <GeneralInstructions
                                  instructions={sub.instructions}
                                  placeholderValues={placeholderValues}
                                  {...(canEdit
                                    ? {
                                        onSave: (body: string) =>
                                          updateInstructions.mutate({
                                            subsectionId: sub.id,
                                            body,
                                            version: data.version,
                                          }),
                                      }
                                    : {})}
                                />
                              ),
                            }
                          : {})}
                      />
                    ) : (
                      // An empty subsection has no rows to hover between, so the
                      // insertion affordance shows as a standing "+ New Test Case" pill.
                      <div className="px-3 py-3">
                        <button
                          type="button"
                          aria-label={`Add Test Case to ${sub.title}`}
                          disabled={createTestCase.isPending}
                          onClick={() => addTestCase(sub.id)}
                          className="inline-flex h-[22px] cursor-pointer items-center gap-1 rounded-pill bg-[#4c70f0] px-3 text-center font-['IBMPlexSansDevanagari'] text-[12px] font-normal leading-[1.4] tracking-[-0.15px] text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          + New Test Case
                        </button>
                      </div>
                    )}
                  </div>
                </SubsectionRow>
              );

              if (!canDrag) {
                return (
                  <Fragment key={sub.id}>
                    {subsectionRow()}
                    {showCreators && subsectionCreator(section.id, sub.id)}
                  </Fragment>
                );
              }
              // Sortable subsection + the whole row as an append drop target for
              // test-case drags (works collapsed — the drag-out-of-Unsorted flow).
              return (
                <SortableNode key={sub.id} id={sub.id} kind="subsection" label={sub.title}>
                  {({ handleProps, listeners }) => (
                    <>
                      <CaseDropTarget
                        subsectionId={sub.id}
                        kind="subsectionDrop"
                        className="border-b border-border"
                      >
                        {subsectionRow(
                          <DragHandle label={sub.title} handleProps={handleProps} srOnly />,
                          listeners,
                        )}
                      </CaseDropTarget>
                      {showCreators && subsectionCreator(section.id, sub.id)}
                    </>
                  )}
                </SortableNode>
              );
            });
            return canDrag ? (
              <SortableList ids={section.subsections.map((sub) => sub.id)}>
                {subsectionItems}
              </SortableList>
            ) : (
              subsectionItems
            );
          })()}
        </div>
      ),
    };
  });

  // The dnd translation layer needs the tree's ids (always the FULL tree —
  // moves operate on real positions, and dragging is off while filtering).
  const shape = {
    sections: data.sections.map((section) => ({
      id: section.id,
      subsections: section.subsections.map((sub) => ({
        id: sub.id,
        testCaseIds: sub.testCases.map((testCase) => testCase.id),
      })),
    })),
  };

  return (
    <>
      {pendingExclude && (
        // F-145 (US2, FR-005/014): the warn-and-confirm guard in the house Dialog
        // — focus-trapped, Esc/backdrop-dismissible, labelled. Cancel (or any
        // dismissal) leaves every case unchanged; "Exclude anyway" runs the cascade.
        <Dialog
          open
          onClose={() => setPendingExclude(null)}
          title={`Exclude “${pendingExclude.subsectionTitle}”?`}
        >
          <ExcludeSubsectionConfirm
            subsectionTitle={pendingExclude.subsectionTitle}
            approvedCases={pendingExclude.approvedCases}
            onCancel={() => setPendingExclude(null)}
            onConfirm={confirmExcludeSubsection}
          />
        </Dialog>
      )}
      {moveError && (
        <div
          role="alert"
          className="mx-6 mt-3 flex items-center justify-between gap-3 rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg"
        >
          <span>{moveError}</span>
          <button
            type="button"
            aria-label="Dismiss move error"
            className="cursor-pointer border-0 bg-transparent p-0 font-semibold text-danger-fg"
            onClick={() => setMoveError(null)}
          >
            ✕
          </button>
        </div>
      )}
      {caseActionError && (
        <div
          role="alert"
          className="mx-6 mt-3 flex items-center justify-between gap-3 rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg"
        >
          <span>{caseActionError}</span>
          <button
            type="button"
            aria-label="Dismiss error"
            className="cursor-pointer border-0 bg-transparent p-0 font-semibold text-danger-fg"
            onClick={() => setCaseActionError(null)}
          >
            ✕
          </button>
        </div>
      )}
      {/* F-130 (US4): drag reorder is intentionally paused while the view is
          altered (search / sort / hide) — positions in a transformed tree are
          not real drag targets. Surface a NON-blocking notice so the affordance
          never just silently vanishes (the regression: the global navbar search
          quietly removed every handle). Editor-only — a viewer never reorders. */}
      {canEdit && viewAltered && (
        <div
          role="status"
          className="mx-6 mt-3 rounded-md bg-ink-50 px-3 py-2 text-sm text-text-secondary"
        >
          Reordering is paused while you’re searching, sorting, or hiding completed test cases.
          Clear them to reorder.
        </div>
      )}
      <AssemblyDnd
        enabled={canDrag}
        busy={moveBusy}
        shape={shape}
        onMoveSection={handleMoveSection}
        onMoveSubsection={handleMoveSubsection}
        onMoveTestCase={handleMoveTestCase}
      >
        <UATPage
          stats={statsLine(summariseSelection(data))}
          marked={markedProgress(data)}
          sections={sections}
          initialOpenIds={[...initialOpenSectionIds]}
          {...(onOpenSectionIdsChange && { onOpenIdsChange: onOpenSectionIdsChange })}
          searchPlaceholder="Search by name or Test ID"
          searchValue={query}
          onSearchChange={handleQueryChange}
          forceAllOpen={filtering}
          sectionsSortable={canDrag}
          hideActive={hideCompleted}
          onHideToggle={() => setHideCompleted((active) => !active)}
          sortOrder={sortOrder}
          onSortCycle={cycleSort}
          {...(emptyReason === 'search'
            ? {
                beforeRows: (
                  <p role="status" className="px-4 py-3 text-md text-text-secondary">
                    No test cases match “{query.trim()}”.
                  </p>
                ),
              }
            : emptyReason === 'hidden'
              ? {
                  beforeRows: (
                    <p role="status" className="px-4 py-3 text-md text-text-secondary">
                      All test cases here are complete and hidden — click Hide to show them.
                    </p>
                  ),
                }
              : showCreators
                ? { beforeRows: sectionCreator(undefined) }
                : {})}
        />
      </AssemblyDnd>
    </>
  );
}
